import { PassThrough } from 'node:stream';

/**
 * The CLI's single keyboard source.
 *
 * Bun's node-compat `process.stdin` (≤1.3.x, verified on 1.3.10) starves on a single TTY chunk
 * larger than ~37 bytes: the first ~37 bytes are delivered, the remainder sits unread until the
 * NEXT chunk arrives (seconds-to-forever later). Fast typing bursts, any paste, and multi-byte
 * scripts (Hebrew = 2 bytes/char) all trip it — Enter "doesn't work" (the `\r` starves in the
 * tail) and bracketed pastes freeze mid-paste (the ESC[201~ end marker starves).
 * `Bun.stdin.stream()` delivers the same chunks intact and instantly, so under Bun on a TTY we
 * pump it into a PassThrough that mimics the bits of ReadStream the editor uses (isTTY/isRaw/
 * setRawMode). Everything keyboard-shaped MUST read from `keyInput` — a second reader on the
 * real fd would steal bytes from the pump. Repro: .tmp/qa-input-dx-repro/ · spec:
 * .tmp/tasks/input-line-dx.md. Re-check on Bun upgrades.
 */
export type KeyInput = NodeJS.ReadStream;

let cancelPump: (() => void) | undefined;

export const keyInput: KeyInput = (() => {
  const bun = (globalThis as { Bun?: { stdin: { stream(): ReadableStream<Uint8Array> } } }).Bun;
  if (!bun || !process.stdin.isTTY) return process.stdin;
  const pt = new PassThrough() as unknown as NodeJS.ReadStream & PassThrough;
  pt.isTTY = true;
  pt.setRawMode = (mode: boolean) => { process.stdin.setRawMode(mode); return pt; };
  Object.defineProperty(pt, 'isRaw', { get: () => process.stdin.isRaw });
  // LAZY pump: the pending native read keeps the event loop alive, so starting it at import time
  // would hang one-shot invocations (--help, -p) that never touch the keyboard. First resume()
  // (the editor/menu's "start reading keys") starts it; releaseKeyInput() cancels it for exit.
  let pump: (() => void) | undefined = () => {
    pump = undefined;
    const reader = bun.stdin.stream().getReader();
    let stopped = false;
    cancelPump = () => { stopped = true; void reader.cancel().catch(() => { /* already closed */ }); };
    void (async () => {
      try {
        while (!stopped) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value?.length) pt.write(Buffer.from(value));
        }
      } catch { /* cancelled / fd closed */ }
      pt.end();
    })();
  };
  const ptResume = pt.resume.bind(pt);
  pt.resume = () => { pump?.(); return ptResume(); };
  return pt;
})();

/** Stop the pump so the process can exit (the pending native read keeps the event loop alive). */
export function releaseKeyInput(): void { cancelPump?.(); }
